import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Client } from '@stomp/stompjs';
import { useCurrentUser } from '../contexts/AuthContext';
import { apiWorker } from '../api/worker';
import { Channel, Message } from '../types';
import { SHOULD_USE_MOCKS } from '../lib/clerk';

export const useChannels = (nodeId: string) => {
  return useQuery<Channel[]>({
    queryKey: ['channels', nodeId],
    queryFn: () => apiWorker.get(`/api/nodes/${nodeId}/channels`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!nodeId && nodeId !== 'platform-root',
  });
};

export const useMessages = (channelId: string, cursor?: string) => {
  return useQuery<Message[]>({
    queryKey: ['messages', channelId, cursor],
    queryFn: () => 
      apiWorker.get(`/api/channels/${channelId}/messages`, {
        params: { size: 50, page: cursor ? parseInt(cursor, 10) : 0 }
      }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!channelId,
  });
};

export const useSendMessage = (channelId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: string) => 
      apiWorker.post(`/api/channels/${channelId}/messages`, { body }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onMutate: async (newBody) => {
      await queryClient.cancelQueries({ queryKey: ['messages', channelId] });
      const previousMessages = queryClient.getQueryData<Message[]>(['messages', channelId]);

      if (previousMessages) {
        const optimisticMessage: Message = {
          id: `temp-${Date.now()}`,
          channelId,
          senderUserId: 'me',
          senderName: 'Me',
          senderAvatarColor: 'blue-600',
          body: newBody,
          createdAt: new Date().toISOString(),
          isPinned: false,
          isPending: true,
        };

        queryClient.setQueryData(['messages', channelId], [optimisticMessage, ...previousMessages]);
      }

      return { previousMessages };
    },
    onError: (err, newBody, context) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(['messages', channelId], context.previousMessages);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', channelId] });
    },
  });
};

export const useUpdateMessage = (channelId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, body }: { messageId: string; body: string }) => 
      apiWorker.patch(`/api/messages/${messageId}`, { body }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onMutate: async ({ messageId, body }) => {
      await queryClient.cancelQueries({ queryKey: ['messages', channelId] });
      const previousMessages = queryClient.getQueryData<Message[]>(['messages', channelId]);

      if (previousMessages) {
        queryClient.setQueryData(['messages', channelId], 
          previousMessages.map(m => m.id === messageId ? { ...m, body, isEdited: true } : m)
        );
      }

      return { previousMessages };
    },
    onError: (err, variables, context) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(['messages', channelId], context.previousMessages);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', channelId] });
    },
  });
};

export const useDeleteMessage = (channelId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => 
      apiWorker.delete(`/api/messages/${messageId}`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onMutate: async (messageId) => {
      await queryClient.cancelQueries({ queryKey: ['messages', channelId] });
      const previousMessages = queryClient.getQueryData<Message[]>(['messages', channelId]);

      if (previousMessages) {
        queryClient.setQueryData(['messages', channelId], 
          previousMessages.filter(m => m.id !== messageId)
        );
      }

      return { previousMessages };
    },
    onError: (err, messageId, context) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(['messages', channelId], context.previousMessages);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', channelId] });
    },
  });
};

export const useCreateChannel = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; channelType: string; isPrivate: boolean; allowedRoles?: string[] }) => 
      apiWorker.post(`/api/channels`, data, { params: { nodeId } }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', nodeId] });
    },
  });
};

export const useCreateDirectChannel = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (targetUserId: string) => 
      apiWorker.post(`/api/chat/channels/direct`, null, { params: { nodeId, targetUserId } }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', nodeId] });
    },
  });
};

export const useWebSocket = (
  channelId: string, 
  onMessage: (msg: Message) => void,
  onMessageUpdated?: (msg: Message) => void,
  onMessageDeleted?: (messageId: string) => void,
  disabled?: boolean
) => {
  const clientRef = React.useRef<Client | null>(null);
  const { user } = useCurrentUser();
  const [isConnected, setIsConnected] = React.useState(false);
  const [presenceCount, setPresenceCount] = React.useState(0);
  const [typingUsers, setTypingUsers] = React.useState<Record<string, string>>({});

  // In mock mode the connection is reported as established once a channel is active
  // (render-phase adjustment instead of the synchronous setState the effect did).
  const [mockConnected, setMockConnected] = React.useState(false);
  if (SHOULD_USE_MOCKS && channelId && !disabled && !mockConnected) {
    setMockConnected(true);
    setIsConnected(true);
  }

  React.useEffect(() => {
    if (typeof window === 'undefined' || !channelId || disabled) return;

    if (SHOULD_USE_MOCKS) {
      return; // Mock connection is simulated via the render-phase adjustment above.
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const brokerURL = `${protocol}//${window.location.host}/ws`;

    const client = new Client({
      brokerURL,
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        setIsConnected(true);
        setPresenceCount((c) => Math.max(1, c));
        // Subscribe to Spring Boot STOMP topic for this channel
        client.subscribe(`/topic/channels.${channelId}`, (frame) => {
          try {
            const payload = JSON.parse(frame.body);
            if (!payload) return;
            if (payload.type === 'TYPING' && payload.userId) {
              setTypingUsers((prev) => {
                const next = { ...prev };
                if (payload.isTyping) {
                  next[payload.userId] = payload.userName || 'Someone';
                } else {
                  delete next[payload.userId];
                }
                return next;
              });
              return;
            }
            if (payload.type === 'PRESENCE' && typeof payload.count === 'number') {
              setPresenceCount(payload.count);
              return;
            }
            if ((payload.type === 'MESSAGE_DELETED' || payload.deleted) && payload.messageId) {
              onMessageDeleted?.(payload.messageId);
              return;
            }
            if (payload.type === 'MESSAGE_UPDATED' || payload.isEdited) {
              if (payload.channelId === channelId) {
                if (onMessageUpdated) onMessageUpdated(payload as Message);
                else onMessage(payload as Message);
              }
              return;
            }
            const message: Message = payload;
            if (message && message.channelId === channelId) {
              onMessage(message);
            }
          } catch (e) {
            console.error('[STOMP] Failed to parse message', e);
          }
        });
      },
      onDisconnect: () => {
        setIsConnected(false);
      },
      onStompError: (frame) => {
        console.warn('[STOMP] Broker error:', frame.headers['message']);
      },
      onWebSocketClose: () => {
        setIsConnected(false);
      }
    });

    client.activate();
    clientRef.current = client;

    return () => {
      client.deactivate();
    };
  }, [channelId, disabled, onMessage, onMessageUpdated, onMessageDeleted]);

  const send = React.useCallback((body: string, replyToId?: string) => {
    if (clientRef.current && isConnected) {
      clientRef.current.publish({
        destination: `/topic/channels.${channelId}`,
        body: JSON.stringify({
          channelId,
          body,
          senderUserId: user?.id || 'me',
          senderName: user?.fullName || 'Me',
          senderAvatarColor: '#0044FF',
          replyToId,
          createdAt: new Date().toISOString(),
          isPinned: false
        })
      });
    }
  }, [channelId, isConnected, user]);

  const setTyping = React.useCallback((isTyping: boolean) => {
    if (clientRef.current && isConnected && channelId) {
      clientRef.current.publish({
        destination: `/topic/channels.${channelId}`,
        body: JSON.stringify({
          type: 'TYPING',
          channelId,
          userId: user?.id || 'me',
          userName: user?.fullName || 'Me',
          isTyping
        })
      });
    }
  }, [channelId, isConnected, user]);

  const updateStatus = React.useCallback((status: 'ONLINE' | 'OFFLINE' | 'AWAY') => {
    if (clientRef.current && isConnected && channelId) {
      clientRef.current.publish({
        destination: `/topic/channels.${channelId}`,
        body: JSON.stringify({
          type: 'STATUS',
          userId: user?.id || 'me',
          status
        })
      });
    }
  }, [channelId, isConnected, user]);

  return { send, isConnected, presenceCount, updateStatus, setTyping, typingUsers };
};
